<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  inboxes: {
    type: Array,
    default: () => [],
  },
  selected: {
    type: Object,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['onChange']);

const selectedInbox = ref(null);
const showInboxesDropdown = ref(false);

const { t } = useI18n();

const renameInbox = inbox => {
  return inbox.email && inbox.email !== inbox.name
    ? `${inbox.name} (${inbox.email})`
    : inbox.name;
};

const selectableInboxes = computed(() => {
  return props.inboxes.map(inbox => ({
    value: inbox,
    label: renameInbox(inbox),
  }));
});

const handleSelectedInbox = event => {
  selectedInbox.value = event.value;
  showInboxesDropdown.value = false;
  emit('onChange', event.value);
};

watch(
  () => props.selected,
  newVal => {
    selectedInbox.value = newVal;
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="flex items-center flex-1 w-full gap-3 px-4 py-1 overflow-y-visible"
  >
    <div class="flex items-center flex-1 w-full gap-3">
      <label
        class="w-[5rem] text-end mb-0.5 text-sm font-medium text-n-slate-11 whitespace-nowrap"
      >
        {{ t('DRAFTS.FORM.INBOX_SELECTOR.LABEL') }}
      </label>
      <div
        v-if="!selectableInboxes.length"
        class="flex items-center gap-1.5 rounded-md bg-n-alpha-2 truncate ltr:pl-3 rtl:pr-3 ltr:pr-1 rtl:pl-1 h-7 min-w-0"
      >
        <span class="text-sm truncate text-n-slate-12">
          {{ t('DRAFTS.FORM.INBOX_SELECTOR.NO_INBOXES') }}
        </span>
      </div>
      <div
        v-else
        v-on-click-outside="() => (showInboxesDropdown = false)"
        class="relative flex items-center h-7"
      >
        <button
          class="w-full h-full px-3 text-sm text-left text-n-slate-12 bg-n-alpha-2 rounded-md hover:bg-n-alpha-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-n-brand"
          @click="showInboxesDropdown = !showInboxesDropdown"
        >
          {{
            selectedInbox
              ? renameInbox(selectedInbox)
              : t('DRAFTS.FORM.INBOX_SELECTOR.PLACEHOLDER')
          }}
        </button>
        <DropdownMenu
          v-if="showInboxesDropdown && !disabled"
          class="top-8"
          :menu-items="selectableInboxes"
          @close="showInboxesDropdown = false"
          @action="handleSelectedInbox($event)"
        />
      </div>
    </div>
  </div>
</template>
